import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Clock, DollarSign, Briefcase, Plane } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ["All", "Firewatch", "Welding", "Structural", "Pipefitting", "Electrical", "Rigging", "Marine Coatings", "Machinist"];

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    axios
      .get(`${API}/jobs`, { params: filter === "All" ? {} : { trade: filter } })
      .then((r) => { if (!ignore) setJobs(r.data); })
      .catch(() => {})
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [filter]);

  return (
    <section id="jobs" className="relative py-24 sm:py-32 border-b border-white/10 bg-[#0B0F14]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3">[ SEC—MARKETPLACE — FIND SHIPYARD JOBS ]</div>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Always hiring across the fleet</h2>
          <p className="mt-4 max-w-2xl text-[#8FA3B8]">Live openings across participating shipyards, ship-repair, defense, and industrial contractors — nationwide. New roles post weekly. Filter by trade; access/clearance readiness is noted per role.</p>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { v: "150+", l: "Roles filled / month" },
              { v: "12+", l: "Shipyard regions" },
              { v: "24/7", l: "Staffing dispatch" },
              { v: "Entry→Lead", l: "Every trade hiring" },
            ].map((s, i) => (
              <div key={i} className="bg-[#10161F] border border-white/10 p-4">
                <div className="font-display font-black text-2xl text-[#FF5E1E]">{s.v}</div>
                <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] mt-1 uppercase">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`job-filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`font-mono-tech text-[10px] tracking-[0.15em] uppercase px-3.5 py-2 border transition-colors ${
                filter === f ? "border-[#FF5E1E] bg-[#FF5E1E] text-[#0B0F14]" : "border-white/15 text-[#8FA3B8] hover:border-white/40 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-44 bg-[#10161F] border border-white/10 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((j, i) => (
              <motion.div
                key={j.id}
                data-testid={`job-card-${j.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.06 }}
                className="group bg-[#10161F] border border-white/10 hover:border-[#FF5E1E]/50 p-5 transition-colors flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-[9px] tracking-[0.15em] text-[#FF5E1E] border border-[#FF5E1E]/30 px-1.5 py-0.5">{j.trade.toUpperCase()}</span>
                  {j.entry_level && <span className="font-mono-tech text-[9px] tracking-[0.15em] text-[#10B981] border border-[#10B981]/30 px-1.5 py-0.5">ENTRY LEVEL</span>}
                </div>
                <h3 className="mt-3 font-display font-bold uppercase text-lg leading-tight">{j.title}</h3>
                <div className="mt-3 space-y-1.5 text-xs text-[#8FA3B8] flex-1">
                  <Row I={MapPin} t={j.location} />
                  <Row I={DollarSign} t={j.pay} />
                  <Row I={Clock} t={j.shift} />
                  <Row I={Briefcase} t={j.experience} />
                  {j.per_diem && <Row I={Plane} t="Per diem available" />}
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8]">{j.clearance}</span>
                  <Button asChild size="sm" className="rounded-none bg-white/10 hover:bg-[#FF5E1E] hover:text-[#0B0F14] text-white font-display font-bold uppercase text-[11px] h-8">
                    <a href="#apply">Apply</a>
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {!loading && jobs.length === 0 && (
          <div data-testid="jobs-empty-pool" className="border border-[#FF5E1E]/30 bg-[#FF5E1E]/5 p-6 sm:p-8 text-center">
            <h3 className="font-display font-black uppercase text-xl">Demand here is high right now</h3>
            <p className="mt-2 text-[#8FA3B8] max-w-xl mx-auto text-sm">No live posting for this exact trade this second — but roles open constantly and recruiters place candidates directly. Join the talent pool and we'll match you the moment a role fits.</p>
            <Button asChild className="mt-4 rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11">
              <a href="#apply">Join The Talent Pool</a>
            </Button>
          </div>
        )}

        {/* Always-visible reassurance so no candidate is deterred by an empty board */}
        <div className="mt-8 border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#8FA3B8]">
            <span className="text-white font-semibold">Don't see your trade or region?</span> New openings post weekly and most placements happen through direct recruiter matching — not the board.
          </p>
          <Button asChild data-testid="jobs-join-pool-button" className="rounded-none bg-white/10 hover:bg-[#FF5E1E] hover:text-[#0B0F14] text-white font-display font-bold uppercase h-11 shrink-0">
            <a href="#apply">Join The Talent Pool</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Row({ I, t }) {
  return <div className="flex items-center gap-2"><I size={13} className="text-[#FF5E1E]" /> {t}</div>;
}
